import { useCallback, useEffect, useRef, useState } from 'react';
import { getApiUrl } from '@/lib/api-config';
import { fetchEntityComments } from '@/lib/entity-api';
import type { GenericComment } from '@/styles/GenericCommentsSection';

/**
 * The one shared "load / add / delete a record's comments" logic behind
 * <GenericCommentsSection> for forms that don't already keep comments in
 * their page's own state. Comments live in the single unified `comments`
 * table (entity_type + entity_id, phase54); reads AND writes go through the
 * API so the backend scopes by company, resolves the authors and fires the
 * comment notification. `audience` is passed
 * through untouched (Team/Client/Vendor from GenericCommentsSection).
 *
 * `entityId` falsy (create mode) => nothing is loaded and add is a no-op.
 */
export function useEntityComments(entityType: string, entityId: string | null | undefined) {
  const [comments, setComments] = useState<GenericComment[]>([]);
  // A read that was already in flight when the user added/deleted a comment is stale: its answer is dropped.
  const readVersion = useRef(0);

  const refetch = useCallback(async () => {
    if (!entityId) {
      setComments([]);
      return;
    }
    const version = ++readVersion.current;
    try {
      const list = ((await fetchEntityComments(entityType, entityId)) as GenericComment[]) || [];
      if (version === readVersion.current) setComments(list);
    } catch (error) {
      console.error(`[useEntityComments:${entityType}] failed to fetch comments`, error);
    }
  }, [entityType, entityId]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const addComment = useCallback(
    async (content: string, audience?: string) => {
      if (!entityId || !content.trim()) return;
      const response = await fetch(getApiUrl('api/entities/comment'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entity_type: entityType, entity_id: entityId, content: content.trim(), ...(audience ? { audience } : {}) }),
      });
      if (!response.ok) {
        console.error(`[useEntityComments:${entityType}] failed to add comment`);
        return;
      }
      // The API returns the saved comment WITH its author: show it right away, no second read of the list.
      const { data: saved } = await response.json();
      readVersion.current++;
      setComments((prev) => [...prev, { ...saved, author: saved.author ?? { id: saved.created_by, display_name: null } } as GenericComment]);
    },
    [entityType, entityId]
  );

  const deleteComment = useCallback(
    async (commentId: string) => {
      const response = await fetch(getApiUrl(`api/entities/comment/${commentId}`), {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!response.ok) {
        console.error(`[useEntityComments:${entityType}] failed to delete comment`);
        return;
      }
      readVersion.current++;
      setComments((prev) => prev.filter((c) => c.id !== commentId));
    },
    [entityType]
  );

  return { comments, addComment, deleteComment, refetch };
}
